import { useState } from "react";
import { emotionOptions, communityPosts } from "@/mocks/communication";
import { CommunityUser } from "@/hooks/useCommunityAuth";

type Post = typeof communityPosts[0] & { whatsapp?: string };

interface Props {
  onClose: () => void;
  onSubmit: (post: Post) => void;
  currentUser?: CommunityUser | null;
}

const roleOptions = [
  "Doctor / Physician",
  "Psychologist / Counselor",
  "Nutritionist",
  "Social Worker",
  "Nurse",
  "Volunteer",
  "Business Owner",
  "Parent / Family",
  "Student",
  "Other",
];

const helpTypeOptions = [
  "Free Medical Advice",
  "Free Consultation",
  "Free Counseling",
  "Free Meals",
  "Needs Support",
  "Donated",
  "Sharing Experience",
  "Looking for Help",
];

export default function PostCreateModal({ onClose, onSubmit, currentUser }: Props) {
  const [name, setName] = useState(currentUser?.name || "");
  const [role, setRole] = useState(currentUser?.role || roleOptions[0]);
  const [selectedEmotion, setSelectedEmotion] = useState(emotionOptions[0]);
  const [helpType, setHelpType] = useState(helpTypeOptions[0]);
  const [content, setContent] = useState("");
  const [shareWhatsApp, setShareWhatsApp] = useState(false);
  const [step, setStep] = useState<"form" | "success">("form");

  const handleSubmit = () => {
    if (!name.trim() || !content.trim()) return;
    const newPost: Post = {
      id: Date.now(),
      user: name,
      avatar: `https://public.readdy.ai/ai/img_res/278010f6f79b901237a6bc2c2af2c6cd.jpg${Date.now()}&orientation=squarish`,
      role,
      country: "🌍",
      tag: helpType === "Needs Support" || helpType === "Looking for Help" ? "Need Help"
        : helpType === "Donated" ? "Donor"
        : helpType.startsWith("Free") ? "Helper"
        : "Helper",
      tagColor: helpType === "Needs Support" || helpType === "Looking for Help" ? "rose"
        : helpType === "Donated" ? "amber"
        : "teal",
      emotion: selectedEmotion.key,
      emotionIcon: selectedEmotion.icon,
      time: "Just now",
      content,
      helpType,
      likes: 0,
      replies: 0,
      helped: 0,
      whatsapp: shareWhatsApp && currentUser?.whatsapp ? currentUser.whatsapp : undefined,
    };
    onSubmit(newPost);
    setStep("success");
  };

  if (step === "success") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-teal-500 text-3xl"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Your Post is Live!</h3>
          <p className="text-stone-500 text-sm mb-2">Your message is now visible to the community.</p>
          {shareWhatsApp && currentUser?.whatsapp && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2">
              <i className="ri-whatsapp-line text-green-500 text-lg"></i>
              <p className="text-green-700 text-xs font-semibold">
                Your WhatsApp is shared — people can connect with you directly!
              </p>
            </div>
          )}
          <p className="text-rose-500 text-sm font-semibold mb-5">
            <i className="ri-heart-fill mr-1"></i>
            Thank you for being part of our caring community!
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-stone-900 text-white font-semibold rounded-lg text-sm cursor-pointer whitespace-nowrap"
          >
            Back to Community Board
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 overflow-y-auto py-6">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg my-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">Post to Community Board</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer">
            <i className="ri-close-line text-stone-500"></i>
          </button>
        </div>

        {/* Logged-in user banner */}
        {currentUser && (
          <div className="bg-pink-50 border border-pink-100 rounded-xl p-3 mb-4 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-pink-400 text-white text-xs font-bold flex-shrink-0">
              {currentUser.name.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900">{currentUser.name}</p>
              <p className="text-xs text-stone-500">{currentUser.role}</p>
            </div>
            <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
              <i className="ri-checkbox-circle-fill"></i> Signed in
            </span>
          </div>
        )}

        {/* Name */}
        {!currentUser && (
          <div className="mb-4">
            <label className="text-sm font-semibold text-gray-700 block mb-1.5">Your Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name or nickname"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-400"
            />
          </div>
        )}

        {/* Role */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">Your Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-400 bg-white cursor-pointer"
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        {/* Emotion */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">How Are You Feeling?</label>
          <div className="flex flex-wrap gap-2">
            {emotionOptions.map((e) => (
              <button
                key={e.key}
                onClick={() => setSelectedEmotion(e)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors cursor-pointer whitespace-nowrap ${
                  selectedEmotion.key === e.key
                    ? "border-teal-400 bg-teal-50 text-teal-700"
                    : "border-stone-200 text-stone-600 hover:border-teal-200"
                }`}
              >
                <i className={`${e.icon} ${e.color}`}></i>
                {e.label}
              </button>
            ))}
          </div>
        </div>

        {/* Help type */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">Post Type</label>
          <div className="flex flex-wrap gap-2">
            {helpTypeOptions.map((h) => (
              <button
                key={h}
                onClick={() => setHelpType(h)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors cursor-pointer whitespace-nowrap ${
                  helpType === h
                    ? "border-rose-400 bg-rose-50 text-rose-600"
                    : "border-stone-200 text-stone-600 hover:border-rose-200"
                }`}
              >
                {h}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">Your Message</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value.slice(0, 500))}
            placeholder="Share what you need, what you can offer, or how you feel. The community is here to help..."
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-400 resize-none h-28"
            maxLength={500}
          />
          <p className="text-xs text-stone-400 text-right">{content.length}/500</p>
        </div>

        {/* WhatsApp share toggle */}
        {currentUser?.whatsapp ? (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="ri-whatsapp-line text-green-500 text-lg"></i>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Share WhatsApp on this post</p>
                  <p className="text-xs text-stone-500">Let others connect with you directly: {currentUser.whatsapp}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShareWhatsApp((v) => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${shareWhatsApp ? "bg-green-500" : "bg-stone-200"}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${shareWhatsApp ? "translate-x-5" : "translate-x-0.5"}`}></span>
              </button>
            </div>
          </div>
        ) : currentUser && !currentUser.whatsapp ? (
          <div className="mb-4 bg-stone-50 border border-stone-200 rounded-xl p-3 flex items-center gap-2">
            <i className="ri-whatsapp-line text-stone-400 text-lg"></i>
            <p className="text-xs text-stone-500">
              No WhatsApp saved on your profile. You can add it when editing your profile.
            </p>
          </div>
        ) : null}

        <div className="bg-rose-50 rounded-xl p-3 mb-5 flex items-start gap-2">
          <i className="ri-heart-fill text-rose-400 mt-0.5 flex-shrink-0"></i>
          <p className="text-rose-600 text-xs">
            This is a caring community. Be kind, be helpful, and if you can — <strong>donate for infants in need around the world.</strong>
          </p>
        </div>

        <button
          onClick={handleSubmit}
          disabled={(!currentUser && !name.trim()) || !content.trim()}
          className="w-full py-3 bg-teal-500 hover:bg-teal-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
        >
          Post to Community Board
        </button>
      </div>
    </div>
  );
}
